import {
    type FC,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Banner,
    BannerType,
    Icon,
    LineChart,
    type LineChartProps,
} from "@onshopfront/components";

export interface ApiApplicationPerformanceBucketType {
    // UTC timestamp for this hour's bucket
    timestamp: string;
    inbound: number;
    outbound: number;
}

export interface ApiApplicationPerformanceDayType {
    date: string;
    inbound: number;
    outbound: number;
}

const formatLocalDateKey = (date: Date): string => (
    `${date.getFullYear()}-` +
    `${String(date.getMonth() + 1).padStart(2, "0")}-` +
    `${String(date.getDate()).padStart(2, "0")}`
);

/**
 * Formats a date as a "YYYY-MM-DD" key for the calendar day it falls on in the given time zone
 */
const formatDateKeyInTimeZone = (date: Date, timeZone: string): string => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year : "numeric",
        month: "2-digit",
        day  : "2-digit",
    }).formatToParts(date);

    const getPart = (type: Intl.DateTimeFormatPartTypes): string => (
        parts.find(part => part.type === type)?.value ?? ""
    );

    return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
};

/**
 * Fills missing days between the first and last with a zero-value entry, so a day with no
 * traffic shows as a gap rather than being skipped over
 */
export const fillMissingDays = (
    days: Array<ApiApplicationPerformanceDayType>
): Array<ApiApplicationPerformanceDayType> => {
    if(days.length < 2) {
        return days;
    }

    const byDate = new Map(days.map(day => [ day.date, day ]));
    const cursor = new Date(`${days[0].date}T00:00:00`);
    const end = new Date(`${days[days.length - 1].date}T00:00:00`);

    const filled: Array<ApiApplicationPerformanceDayType> = [];

    while(cursor <= end) {
        const key = formatLocalDateKey(cursor);

        filled.push(byDate.get(key) ?? { date: key, inbound: 0, outbound: 0 });

        cursor.setDate(cursor.getDate() + 1);
    }

    return filled;
};

/**
 * Groups hourly UTC buckets into calendar days in the given time zone, defaulting to the viewer's
 */
export const groupByLocalDay = (
    buckets: Array<ApiApplicationPerformanceBucketType>,
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
): Array<ApiApplicationPerformanceDayType> => {
    const days = new Map<string, ApiApplicationPerformanceDayType>();

    for(const bucket of buckets) {
        const key = formatDateKeyInTimeZone(new Date(bucket.timestamp), timeZone);
        const existing = days.get(key) ?? { date: key, inbound: 0, outbound: 0 };

        existing.inbound += bucket.inbound;
        existing.outbound += bucket.outbound;

        days.set(key, existing);
    }

    const sorted = Array.from(days.values()).sort((a, b) => a.date.localeCompare(b.date));

    return fillMissingDays(sorted);
};

/**
 * Formats a "YYYY-MM-DD" day key as a short, locale-aware label for the graph's axis/tooltips
 */
const formatDayLabel = (dateKey: string): string => (
    new Date(`${dateKey}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" })
);

const CHART_OPTIONS: LineChartProps["options"] = {
    maintainAspectRatio: false,
    interaction        : {
        mode     : "index",
        intersect: false,
    },
    scales: {
        x: {
            grid : { display: false },
            ticks: {
                callback: function(value) {
                    const label = this.getLabelForValue(value as number);

                    return typeof label === "string" ? formatDayLabel(label) : label;
                },
            },
        },
        y: {
            beginAtZero: true,
            ticks      : { precision: 0 },
            grid       : { color: "rgba(127, 127, 127, 0.15)" },
        },
    },
    plugins: {
        legend: {
            position: "top",
            align   : "end",
            labels  : {
                usePointStyle: true,
                boxWidth     : 8,
                boxHeight    : 8,
            },
        },
    },
    elements: {
        point: { radius: 2, hoverRadius: 4 },
        line : { borderWidth: 2 },
    },
};

interface Props {
    applicationId: string;
}

/**
 * The application's performance graph
 */
export const ApiApplicationPerformance: FC<Props> = ({ applicationId }) => {
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState("");
    const [ days, setDays ] = useState<Array<ApiApplicationPerformanceDayType>>([]);

    // Guards against a slower request for a previously selected application landing after a
    // newer one and overwriting its state
    const requestIdRef = useRef(0);

    useEffect(() => {
        const requestId = ++requestIdRef.current;

        setLoading(true);
        setError("");

        fetch(`/application/${applicationId}/performance`, {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then(async (response) => {
                if(response.status !== 200) {
                    let responseBody;

                    try {
                        responseBody = await response.json();
                    } catch {
                        throw new Error("Unable to parse error from the server");
                    }

                    throw new Error(
                        responseBody.error_code ?
                            `${responseBody.error_code}: ${responseBody.message}` :
                            responseBody.message
                    );
                }

                return response.json();
            })
            .then((data) => {
                if(requestId !== requestIdRef.current) {
                    return;
                }

                setDays(groupByLocalDay(data.data));
            })
            .catch((err: unknown) => {
                if(requestId !== requestIdRef.current) {
                    return;
                }

                setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if(requestId !== requestIdRef.current) {
                    return;
                }

                setLoading(false);
            });
    }, [ applicationId ]);

    const data = useMemo<LineChartProps["data"]>(() => ({
        labels  : days.map(day => day.date),
        datasets: [
            {
                label               : "Requests to Shopfront",
                borderColor         : "#5593bd",
                backgroundColor     : "rgba(85, 147, 189, 0.15)",
                pointBackgroundColor: "#5593bd",
                tension             : 0.3,
                fill                : true,
                data                : days.map(day => day.inbound),
            },
            {
                label               : "Requests from Shopfront",
                borderColor         : "#bd5593",
                backgroundColor     : "rgba(189, 85, 147, 0.15)",
                pointBackgroundColor: "#bd5593",
                tension             : 0.3,
                fill                : true,
                data                : days.map(day => day.outbound),
            },
        ],
    }), [ days ]);

    if(error) {
        return (
            <div>
                <Banner
                    type={BannerType.Error}
                >
                    {error}
                </Banner>
            </div>
        );
    }

    if(loading) {
        return (
            <div>
                <p><Icon icon={[ "fal", "spinner-third" ]} spin />Loading...</p>
            </div>
        );
    }

    if(!days.length) {
        return (
            <div>
                <p>No API calls or webhook deliveries to date...</p>
            </div>
        );
    }

    return (
        <div className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4">
            <LineChart
                data={data}
                options={CHART_OPTIONS}
                height={220}
            />
        </div>
    );
};
